import { logger } from '../../utils/logger';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminNavbar from '../../components/AdminNavbar';
import axios from '../../api/axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit, Trash2, X, Image as ImageIcon, Check, Loader2, DollarSign, Tag, Info } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardFooter } from '../../components/ui/card';

const AdminMenu = () => {
  const { admin } = useAuth();
  const navigate = useNavigate();
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: 'main course',
    availability: 'ready-made',
    image: '',
    isAvailable: true,
    options: [],
    settings: []
  });
  const [newOption, setNewOption] = useState({ name: '', additionalPrice: '' });
  const [newSetting, setNewSetting] = useState({ name: '', options: [] });
  const [newSettingOption, setNewSettingOption] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [additionalImages, setAdditionalImages] = useState([]);
  const [additionalImageFiles, setAdditionalImageFiles] = useState([]);

  useEffect(() => {
    if (!admin) {
      navigate('/admin/login');
      return;
    }
    fetchMenuItems();
  }, [admin, navigate]);

  const fetchMenuItems = async () => {
    try {
      const response = await axios.get('/menu');
      const data = response.data;
      // Handle both array (legacy) and paginated object formats
      const items = Array.isArray(data) ? data : (data.menuItems || []);
      setMenuItems(items);
      setLoading(false);
    } catch (error) {
      logger.error('Error fetching menu:', error);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAdditionalImagesChange = (e) => {
    const files = Array.from(e.target.files);
    const remainingSlots = 5 - additionalImages.length;

    if (files.length > remainingSlots) {
      alert(`You can only add ${remainingSlots} more image(s). Maximum 5 additional images.`);
      return;
    }

    const newFiles = files.slice(0, remainingSlots);
    setAdditionalImageFiles([...additionalImageFiles, ...newFiles]);

    newFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAdditionalImages(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAdditionalImage = (index) => {
    setAdditionalImages(prev => prev.filter((_, i) => i !== index));
    setAdditionalImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadImage = async () => {
    if (!imageFile) return null;
    const formData = new FormData();
    formData.append('image', imageFile);
    try {
      const response = await axios.post('/menu/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data.imageUrl;
    } catch (error) {
      logger.error('Error uploading image:', error);
      throw error;
    }
  };

  const handleAddOption = () => {
    if (newOption.name && newOption.additionalPrice) {
      setFormData({
        ...formData,
        options: [...formData.options, { ...newOption, additionalPrice: Number(newOption.additionalPrice) }]
      });
      setNewOption({ name: '', additionalPrice: '' });
    }
  };

  const handleRemoveOption = (index) => {
    setFormData({
      ...formData,
      options: formData.options.filter((_, i) => i !== index)
    });
  };

  const handleAddSettingOption = () => {
    const optionToAdd = newSettingOption.trim();
    if (optionToAdd) {
      if (newSetting.options.includes(optionToAdd)) {
        alert("This option already exists!");
        return;
      }
      setNewSetting({
        ...newSetting,
        options: [...newSetting.options, optionToAdd]
      });
      setNewSettingOption('');
    }
  };

  const handleRemoveSettingOption = (optionIndex) => {
    setNewSetting({
      ...newSetting,
      options: newSetting.options.filter((_, i) => i !== optionIndex)
    });
  };

  const handleAddSetting = () => {
    if (newSetting.name.trim() && newSetting.options.length > 0) {
      setFormData({
        ...formData,
        settings: [...formData.settings, { ...newSetting, _id: Date.now() }]
      });
      setNewSetting({ name: '', options: [] });
      setNewSettingOption('');
    }
  };

  const handleRemoveSetting = (index) => {
    setFormData({
      ...formData,
      settings: formData.settings.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let imageUrl = formData.image;
      if (imageFile) {
        imageUrl = await uploadImage();
      }

      const additionalImageUrls = [];
      for (const file of additionalImageFiles) {
        const formData = new FormData();
        formData.append('image', file);
        try {
          const response = await axios.post('/menu/upload-image', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          additionalImageUrls.push(response.data.imageUrl);
        } catch (error) {
          logger.error('Error uploading additional image:', error);
        }
      }

      const allAdditionalImages = editingItem
        ? [...(formData.additionalImages || []), ...additionalImageUrls]
        : additionalImageUrls;

      const data = {
        ...formData,
        price: Number(formData.price),
        image: imageUrl || '',
        additionalImages: allAdditionalImages.length > 0 ? allAdditionalImages : null
      };

      if (editingItem) {
        const itemId = editingItem.id || editingItem._id;
        await axios.put(`/menu/${itemId}`, data);
      } else {
        await axios.post('/menu', data);
      }

      fetchMenuItems();
      resetForm();
      setShowModal(false);
    } catch (error) {
      logger.error('Error saving menu item:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Failed to save menu item';
      alert(`Error: ${errorMsg}`);
    }
  };

  const handleEdit = (item) => {
    const itemWithId = { ...item, _id: item.id || item._id };
    setEditingItem(itemWithId);
    setFormData({
      name: item.name,
      description: item.description,
      price: item.price,
      category: item.category,
      availability: item.availability,
      image: item.image || '',
      isAvailable: item.is_available !== undefined ? item.is_available : true,
      options: item.options || [],
      settings: item.settings || [],
      additionalImages: item.additional_images || []
    });
    setImageFile(null);
    setImagePreview(item.image || '');
    setAdditionalImages(item.additional_images || []);
    setAdditionalImageFiles([]);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await axios.delete(`/menu/${id}`);
        fetchMenuItems();
      } catch (error) {
        logger.error('Error deleting menu item:', error);
        alert('Failed to delete menu item');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      category: 'main course',
      availability: 'ready-made',
      image: '',
      isAvailable: true,
      options: [],
      settings: [],
      additionalImages: []
    });
    setEditingItem(null);
    setNewOption({ name: '', additionalPrice: '' });
    setNewSetting({ name: '', options: [] });
    setNewSettingOption('');
    setImageFile(null);
    setImagePreview('');
    setAdditionalImages([]);
    setAdditionalImageFiles([]);
  };

  if (!admin) return null;

  return (
    <div className="min-h-screen bg-background pb-20">
      <AdminNavbar />

      <div className="container mx-auto px-6 py-8">
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
          <div>
            <h1 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gold-400 to-gold-200">
              Menu Management
            </h1>
            <p className="text-gray-400 mt-2">Manage your restaurant offerings and pricing</p>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="bg-gold-500 text-black hover:bg-gold-400 font-bold px-6 h-12 rounded-full shadow-lg shadow-gold-500/20 transition-all hover:scale-105"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add New Item
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-12 h-12 text-gold-500 animate-spin" />
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
          >
            {Array.isArray(menuItems) && menuItems.map((item, index) => (
              <motion.div
                key={item.id || item._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="overflow-hidden bg-black/40 border-gold-500/20 backdrop-blur-sm hover:border-gold-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-gold-500/10 group h-full flex flex-col">
                  <div className="relative h-48 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent z-10 opacity-60" />
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                        <ImageIcon className="w-12 h-12 text-gray-600" />
                      </div>
                    )}
                    <div className="absolute top-3 right-3 z-20">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold shadow-lg flex items-center gap-1 ${item.isAvailable ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
                        {item.isAvailable ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                        {item.isAvailable ? 'Active' : 'Hidden'}
                      </span>
                    </div>
                    <div className="absolute bottom-3 left-3 z-20">
                      <span className="px-3 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/10 text-white text-xs font-medium">
                        {item.category}
                      </span>
                    </div>
                  </div>

                  <CardContent className="p-5 flex-1">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-xl font-bold text-gold-200 line-clamp-1">{item.name}</h3>
                      <span className="text-gold-400 font-bold whitespace-nowrap">Rs. {Math.round(item.price).toLocaleString()}</span>
                    </div>
                    <p className="text-gray-400 text-sm line-clamp-2 mb-4 h-10">{item.description}</p>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {(item.options?.length > 0 || item.settings?.length > 0) && (
                        <div className="flex gap-2">
                          {item.options?.length > 0 && <span className="text-xs bg-blue-500/10 text-blue-400 px-2 py-1 rounded border border-blue-500/20">{item.options.length} Add-ons</span>}
                          {item.settings?.length > 0 && <span className="text-xs bg-purple-500/10 text-purple-400 px-2 py-1 rounded border border-purple-500/20">{item.settings.length} Settings</span>}
                        </div>
                      )}
                    </div>
                  </CardContent>

                  <CardFooter className="p-4 pt-0 grid grid-cols-2 gap-3 mt-auto">
                    <Button
                      variant="outline"
                      onClick={() => handleEdit(item)}
                      className="w-full border-blue-500/30 text-blue-400 hover:text-white hover:bg-blue-600 hover:border-transparent transition-all"
                    >
                      <Edit className="w-4 h-4 mr-2" /> Edit
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleDelete(item.id || item._id)}
                      className="w-full border-red-500/30 text-red-400 hover:text-white hover:bg-red-600 hover:border-transparent transition-all"
                    >
                      <Trash2 className="w-4 h-4 mr-2" /> Delete
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>

      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => { setShowModal(false); resetForm(); }}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              className="relative bg-gray-900 border border-gold-500/30 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
            >
              <div className="flex justify-between items-center p-6 border-b border-white/10 bg-black/40">
                <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gold-400 to-gold-200">
                  {editingItem ? 'Edit Item' : 'Create New Item'}
                </h2>
                <Button variant="ghost" size="icon" onClick={() => { setShowModal(false); resetForm(); }} className="text-gray-400 hover:text-white">
                  <X className="w-6 h-6" />
                </Button>
              </div>

              <div className="p-6 overflow-y-auto custom-scrollbar">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Basic Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gold-400">Item Name</label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="e.g., Royal Burger"
                        className="bg-black/20 border-white/10 focus:border-gold-500/50"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gold-400 flex items-center gap-2">
                        <DollarSign className="w-4 h-4" /> Price (Rs)
                      </label>
                      <Input
                        type="number"
                        name="price"
                        value={formData.price}
                        onChange={handleInputChange}
                        required
                        max="9999999"
                        className="bg-black/20 border-white/10 focus:border-gold-500/50"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gold-400 flex items-center gap-2">
                      <Info className="w-4 h-4" /> Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      required
                      rows="3"
                      className="w-full rounded-md border border-white/10 bg-black/20 px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gold-500/50"
                      placeholder="Describe the dish..."
                    />
                  </div>

                  {/* Category & Availability */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gold-400 flex items-center gap-2">
                        <Tag className="w-4 h-4" /> Category
                      </label>
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        className="w-full h-10 rounded-md border border-white/10 bg-black/20 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-gold-500/50"
                      >
                        <option value="desserts">Desserts</option>
                        <option value="beverages">Beverages</option>
                        <option value="baked items">Baked Items</option>
                        <option value="main course">Main Course</option>
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gold-400">Order Type</label>
                      <select
                        name="availability"
                        value={formData.availability}
                        onChange={handleInputChange}
                        className="w-full h-10 rounded-md border border-white/10 bg-black/20 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-gold-500/50"
                      >
                        <option value="ready-made">Ready-Made</option>
                        <option value="pre-order">Pre-Order</option>
                      </select>
                    </div>
                  </div>

                  {/* Image Upload */}
                  <div className="space-y-4 border border-white/10 rounded-lg p-4 bg-black/20">
                    <label className="text-sm font-medium text-gold-400 flex items-center gap-2">
                      <ImageIcon className="w-4 h-4" /> Main Image
                    </label>
                    <div className="flex items-start gap-4">
                      {imagePreview && (
                        <img src={imagePreview} alt="Preview" className="w-24 h-24 object-cover rounded-lg border border-gold-500/30" />
                      )}
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="bg-transparent border-white/10 cursor-pointer file:bg-gold-500/10 file:text-gold-500 file:border-0 file:rounded-md file:px-2 file:py-1 file:mr-4 file:font-semibold hover:file:bg-gold-500/20"
                      />
                    </div>
                  </div>

                  {/* Additional Images Upload */}
                  <div className="space-y-4 border border-white/10 rounded-lg p-4 bg-black/20">
                    <label className="text-sm font-medium text-gold-400 flex items-center gap-2">
                      <ImageIcon className="w-4 h-4" /> Additional Images ({additionalImages.length}/5)
                    </label>
                    <div className="flex flex-col gap-4">
                      <Input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleAdditionalImagesChange}
                        disabled={additionalImages.length >= 5}
                        className="bg-transparent border-white/10 cursor-pointer file:bg-gold-500/10 file:text-gold-500 file:border-0 file:rounded-md file:px-2 file:py-1 file:mr-4 file:font-semibold hover:file:bg-gold-500/20 disabled:opacity-50"
                      />
                      {additionalImages.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {additionalImages.map((img, idx) => (
                            <div key={idx} className="relative group w-20 h-20">
                              <img src={img} alt={`Extra ${idx}`} className="w-full h-full object-cover rounded-md border border-white/20" />
                              <button
                                type="button"
                                onClick={() => removeAdditionalImage(idx)}
                                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Status Toggle */}
                  <div className="flex items-center justify-between bg-black/20 p-4 rounded-lg border border-white/10">
                    <span className="text-white font-medium">Visibility Status</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="isAvailable"
                        checked={formData.isAvailable}
                        onChange={handleInputChange}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                      <span className="ml-3 text-sm font-medium text-gray-300">
                        {formData.isAvailable ? 'Visible to Customers' : 'Hidden'}
                      </span>
                    </label>
                  </div>

                  {/* Options & Settings */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Paid Add-ons */}
                    <div className="space-y-4 border border-white/10 rounded-lg p-4 bg-black/20">
                      <h3 className="text-white font-semibold flex items-center gap-2">
                        <Plus className="w-4 h-4 text-green-400" /> Paid Add-ons
                      </h3>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Option Name"
                          value={newOption.name}
                          onChange={(e) => setNewOption({ ...newOption, name: e.target.value })}
                          className="bg-black/40 border-white/10 h-9"
                        />
                        <Input
                          type="number"
                          placeholder="Price"
                          value={newOption.additionalPrice}
                          onChange={(e) => setNewOption({ ...newOption, additionalPrice: e.target.value })}
                          className="w-24 bg-black/40 border-white/10 h-9"
                        />
                        <Button type="button" size="sm" onClick={handleAddOption} className="bg-green-600 hover:bg-green-700">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {formData.options.map((opt, idx) => (
                          <div key={idx} className="flex justify-between items-center bg-white/5 px-3 py-2 rounded text-sm">
                            <span className="text-gray-300">{opt.name} (+{Math.round(opt.additionalPrice)})</span>
                            <button type="button" onClick={() => handleRemoveOption(idx)} className="text-red-400 hover:text-red-300">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Free Customizations */}
                    <div className="space-y-4 border border-white/10 rounded-lg p-4 bg-black/20">
                      <h3 className="text-white font-semibold flex items-center gap-2">
                        <Edit className="w-4 h-4 text-purple-400" /> Free Settings
                      </h3>
                      <Input
                        placeholder="Setting Name (e.g. Spice Level)"
                        value={newSetting.name}
                        onChange={(e) => setNewSetting({ ...newSetting, name: e.target.value })}
                        className="bg-black/40 border-white/10 h-9 mb-2"
                      />
                      <div className="flex gap-2">
                        <Input
                          placeholder="Option (e.g. Mild)"
                          value={newSettingOption}
                          onChange={(e) => setNewSettingOption(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSettingOption())}
                          className="bg-black/40 border-white/10 h-9"
                        />
                        <Button type="button" size="sm" onClick={handleAddSettingOption} className="bg-purple-600 hover:bg-purple-700">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {newSetting.options.map((opt, idx) => (
                          <span key={idx} className="bg-purple-500/20 text-purple-300 text-xs px-2 py-1 rounded flex items-center gap-1">
                            {opt} <X className="w-3 h-3 cursor-pointer" onClick={() => handleRemoveSettingOption(idx)} />
                          </span>
                        ))}
                      </div>
                      <Button
                        type="button"
                        onClick={() => {
                          // Auto-add pending option if present
                          let currentOptions = [...newSetting.options];
                          const pendingOption = newSettingOption.trim();

                          if (pendingOption) {
                            if (!currentOptions.includes(pendingOption)) {
                              currentOptions.push(pendingOption);
                            } else {
                              // If trying to add a duplicate as the only action, warn the user
                              if (currentOptions.length === 0) { // Should technically be impossible if it includes it, but logical safety
                                alert("This option is already added.");
                                return;
                              }
                            }
                          }

                          if (currentOptions.length === 0) {
                            alert("Please add at least one option (e.g. Mild, Spicy).");
                            return;
                          }

                          // Add the setting group
                          setFormData({
                            ...formData,
                            settings: [...formData.settings, { name: newSetting.name, options: currentOptions, _id: Date.now() }]
                          });
                          setNewSetting({ name: '', options: [] });
                          setNewSettingOption('');
                        }}
                        disabled={!newSetting.name}
                        className={`w-full h-8 text-xs transition-colors ${!newSetting.name ? 'bg-purple-600/30 text-gray-400 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-500 text-white'}`}
                      >
                        Add Setting Group
                      </Button>
                      <div className="space-y-2 max-h-32 overflow-y-auto mt-2">
                        {formData.settings.map((setting, idx) => (
                          <div key={idx} className="bg-white/5 p-2 rounded text-xs">
                            <div className="flex justify-between">
                              <span className="font-bold text-gray-300">{setting.name}</span>
                              <Trash2 className="w-3 h-3 text-red-400 cursor-pointer" onClick={() => handleRemoveSetting(idx)} />
                            </div>
                            <div className="text-gray-500">{setting.options.join(', ')}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4 pt-4 border-t border-white/10">
                    <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }} className="flex-1">
                      Cancel
                    </Button>
                    <Button type="submit" className="flex-1 bg-gradient-to-r from-gold-600 to-gold-500 text-black font-bold hover:shadow-lg hover:shadow-gold-500/20">
                      {editingItem ? 'Save Changes' : 'Create Item'}
                    </Button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminMenu;


